import { useEffect, type RefObject } from 'react';
import { keyboardMode, keyboardProbe } from './keyboard-probe';

/** Below this a change of the visible height is a browser bar, not a keyboard. */
const KEYBOARD_MIN_PX = 100;
/**
 * The first keyboard on a device, before one was measured. Rather too much than too little: too much is put right as
 * the keyboard is reported (~76 ms after the tap), too little lets the browser push the page up for good
 * (keyboard-probe.ts). Samsung's took 0.42 of a Galaxy's page in the installed app, 0.52 in the browser with its bars.
 */
const FIRST_GUESS = 0.5;
/** A tapped input whose keyboard has not come by then (a hardware keyboard, none shown) drops its lift. */
const RISE_MS = 1500;
/** The keyboard heights measured on this device, per orientation: the first tap after a start lifts by the right one. */
const SAVED_KEY = 'sd.keyboard-height';

type Orientation = 'portrait' | 'landscape';

function loadHeights(): Map<Orientation, number> {
  try {
    const saved = JSON.parse(localStorage.getItem(SAVED_KEY) ?? '{}') as Partial<Record<Orientation, unknown>>;
    return new Map(
      (['portrait', 'landscape'] as const).flatMap((o) => {
        const px = saved[o];
        return typeof px === 'number' && px >= KEYBOARD_MIN_PX ? [[o, px] as const] : [];
      }),
    );
  } catch {
    return new Map();
  }
}

function saveHeights(heights: Map<Orientation, number>) {
  try {
    localStorage.setItem(SAVED_KEY, JSON.stringify(Object.fromEntries(heights)));
  } catch {
    // Private mode or storage off: measured again next time.
  }
}

/**
 * The phone keyboard and the messenger, with the page left its full height (index.html: interactive-widget=
 * resizes-visual). A browser that shortened the page for the keyboard did so as the keyboard started to rise, and the
 * space between showed the background for ~0.2 s (Samsung Internet recording, 2026-10-30); one that only narrows the
 * visible part pushes the page up to show the focused input if the keyboard would cover it.
 *
 * So: the moment a tap gives the message input the focus, `html.kb-lift` and `--kb-lift` (messenger.css) raise the
 * messenger by the keyboard's height at once, before the keyboard starts; the space below takes the keyboard's colour
 * and the keyboard rises over it. The height is the one last measured on this device (per orientation, kept in
 * localStorage), the very first time a guess corrected once the keyboard is up. At once, not animated: a messenger
 * still on its way when the keyboard started was covered, the browser pushed the page up, and the two corrections
 * showed the background again (0.1.33). On the tap's end, not its start: an input lifted from under the resting finger
 * missed the tap (0.1.32). Only a tap: opening a conversation focuses the input without a keyboard. A field in a dialog
 * is lifted once the keyboard is up (`--kb-modal`). A browser that shortens the page anyway is left to do it.
 *
 * Moving with the keyboard as a native app does (Telegram) is not possible: the browser reports the keyboard once, at
 * its final height, and none of its way up (keyboard-probe.ts, `?kb=debug`).
 */
export function useKeyboardLift(input: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport || window.matchMedia?.('(pointer: coarse)').matches !== true) return undefined;
    const root = document.documentElement;
    const mode = keyboardMode();
    const probe = mode ? keyboardProbe(mode) : null;
    /** The page's size with no keyboard up. */
    let full = window.innerHeight;
    let width = window.innerWidth;
    let shortens = false;
    /** The finger that went down on the input, until its tap gave the focus. */
    let pressed = false;
    let rising = 0;
    const heights = loadHeights();
    const orientation = (): Orientation => (window.innerWidth > window.innerHeight ? 'landscape' : 'portrait');
    const lifted = () => root.classList.contains('kb-lift');
    const lift = (px: number) => {
      root.style.setProperty('--kb-lift', `${Math.round(px)}px`);
      root.classList.toggle('kb-lift', px > 0);
    };
    const liftDialog = (px: number) => root.style.setProperty('--kb-modal', `${Math.round(px)}px`);
    /** The keyboard's height now, wherever the browser scrolled the visible part to. */
    const keyboardHeight = () => Math.max(0, window.innerHeight - viewport.height);
    const stopRising = () => {
      window.clearTimeout(rising);
      rising = 0;
    };

    const ahead = () => {
      pressed = false;
      if (shortens || lifted()) return;
      lift(heights.get(orientation()) ?? full * FIRST_GUESS);
      probe?.log(`lift ahead ${root.style.getPropertyValue('--kb-lift')}`);
      stopRising();
      rising = window.setTimeout(() => {
        rising = 0;
        if (keyboardHeight() < KEYBOARD_MIN_PX) lift(0);
      }, RISE_MS);
    };
    const onPress = (event: PointerEvent) => {
      pressed = event.target === input.current;
    };
    // The tap's end: the focus, or for an input that had it with the keyboard down (back button) the click.
    const onFocusIn = (event: FocusEvent) => {
      if (event.target !== input.current) return;
      probe?.log(pressed ? 'focus by tap' : 'focus', true);
      if (pressed) ahead();
    };
    const onClick = (event: MouseEvent) => {
      if (event.target === input.current && document.activeElement === input.current) ahead();
    };
    const onFocusOut = (event: FocusEvent) => {
      if (event.target === input.current) {
        probe?.log('blur', true);
        stopRising();
        lift(0);
      }
      liftDialog(0);
    };
    const onViewport = (event: Event) => {
      probe?.log(`${event.type} H${window.innerHeight} vh${Math.round(viewport.height)} top${Math.round(viewport.offsetTop)}`);
      // Pinched: the visible part is smaller for the zoom, not for a keyboard.
      if (Math.abs(viewport.scale - 1) > 0.01) return;
      if (window.innerWidth !== width) {
        // Turned: a new full size, measured once the keyboard is down.
        width = window.innerWidth;
        if (keyboardHeight() < KEYBOARD_MIN_PX) full = window.innerHeight;
      } else if (window.innerHeight < full - KEYBOARD_MIN_PX) {
        // The browser shortened the page itself: it does the keyboard's work this session.
        shortens = true;
        stopRising();
        lift(0);
        liftDialog(0);
        return;
      }
      const keyboard = keyboardHeight();
      if (keyboard >= KEYBOARD_MIN_PX) {
        stopRising();
        if (heights.get(orientation()) !== keyboard) {
          heights.set(orientation(), keyboard);
          saveHeights(heights);
        }
        const active = document.activeElement;
        const ours = active === input.current || (active instanceof HTMLElement && active.closest('.modal') !== null);
        if (active === input.current) lift(keyboard);
        else if (ours) liftDialog(keyboard);
        // A guess too small let the browser push the page up to show the input: lifted now, back to the top. Other
        // fields (a form further down a page) keep the browser's scrolling.
        if (ours && viewport.offsetTop > 0) window.scrollTo(0, 0);
        return;
      }
      // Tapped, the keyboard still on its way.
      if (rising) return;
      // Down, also by the back button while the input keeps the focus.
      lift(0);
      liftDialog(0);
      full = window.innerHeight;
    };
    document.addEventListener('pointerdown', onPress);
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('click', onClick);
    document.addEventListener('focusout', onFocusOut);
    viewport.addEventListener('resize', onViewport);
    viewport.addEventListener('scroll', onViewport);
    return () => {
      document.removeEventListener('pointerdown', onPress);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('click', onClick);
      document.removeEventListener('focusout', onFocusOut);
      viewport.removeEventListener('resize', onViewport);
      viewport.removeEventListener('scroll', onViewport);
      stopRising();
      lift(0);
      liftDialog(0);
      probe?.stop();
    };
  }, [input]);
}
